import { Box, Card, CardContent, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TFunction } from 'i18next';
import { FC } from 'react';
import { Link } from 'react-router-dom';
import { OrderListingType } from 'types/order';

const formatSearchCurrency = (value: number) => new Intl.NumberFormat('en-US', {
  currency: 'USD',
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
  style: 'currency',
}).format(value);

const useStyles = makeStyles((theme: any) => ({
  card: {
    marginBottom: theme.spacing(1.5),
    cursor: 'pointer',
  },
  link: {
    textDecoration: 'none',
    color: 'inherit',
  },
  cardContent: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: theme.spacing(1.25),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  label: {
    fontSize: '0.875rem',
    color: theme.palette.primary.light,
    fontWeight: 400,
    marginRight: theme.spacing(0.5),
  },
  value: {
    fontSize: '0.875rem',
    color: theme.palette.primary.darker,
    fontWeight: 400,
    overflowWrap: 'anywhere',
    flex: 1,
    textAlign: 'right',
  },
  orderId: {
    fontFamily: '"Roboto Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
    fontVariantNumeric: 'tabular-nums',
  },
  numericValue: {
    fontFamily: '"Roboto Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
    fontVariantNumeric: 'tabular-nums',
  },
  moneyRow: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    columnGap: theme.spacing(1),
    rowGap: theme.spacing(0.5),
  },
  moneyItem: {
    flex: '1 1 auto',
    minWidth: '45%',
    textAlign: 'right',
  },
  moneyLabel: {
    display: 'block',
    fontSize: '0.75rem',
    color: theme.palette.primary.light,
    fontWeight: 400,
    marginBottom: theme.spacing(0.25),
  },
  moneyValue: {
    display: 'block',
    fontSize: '0.875rem',
    color: theme.palette.primary.darker,
    fontWeight: 400,
  },
}));

type OrderCardProps = {
  t: TFunction;
  order: OrderListingType;
  handleSelectRow: (id: string, code: string) => void;
  showAgency: boolean;
};

const OrderCard: FC<OrderCardProps> = ({
  t,
  order,
  handleSelectRow,
  showAgency,
}) => {
  const classes = useStyles();

  const handleClick = () => {
    handleSelectRow(order._id, order.order_id);
  };

  const moneyItems = [
    Number(order.purchase_price) > 0 && (
      <Box key="purchase-price" className={classes.moneyItem}>
        <Typography component="span" className={classes.moneyLabel}>
          {t('orders:purchase-price')}
        </Typography>
        <Typography
          component="span"
          className={`${classes.moneyValue} ${classes.numericValue}`}
        >
          {formatSearchCurrency(Number(order.purchase_price))}
        </Typography>
      </Box>
    ),
    Number(order.loan_amount) > 0 && (
      <Box key="loan-amount" className={classes.moneyItem}>
        <Typography component="span" className={classes.moneyLabel}>
          {t('orders:loan-amount')}
        </Typography>
        <Typography
          component="span"
          className={`${classes.moneyValue} ${classes.numericValue}`}
        >
          {formatSearchCurrency(Number(order.loan_amount))}
        </Typography>
      </Box>
    ),
  ].filter(Boolean);

  return (
    <Link
      to={`/orders/details/${order._id}/overview`}
      className={classes.link}
      onClick={(event) => {
        event.preventDefault();
        handleClick();
      }}
    >
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          {/* Order ID */}
          <Box className={classes.row}>
            <Typography component="span" variant="subtitle2" className={classes.label}>
              {t('orders:order-id')}
            </Typography>
            <Typography
              component="span"
              variant="subtitle1"
              className={`${classes.value} ${classes.orderId}`}
            >
              {order.order_id}
            </Typography>
          </Box>

          {/* Agency - only show when all agencies selected */}
          {showAgency && (
            <Box className={classes.row}>
              <Typography component="span" variant="subtitle2" className={classes.label}>
                {t('common:agency')}
              </Typography>
              <Typography component="span" variant="subtitle1" className={classes.value}>
                {order.agency}
              </Typography>
            </Box>
          )}

          {/* Address */}
          <Box className={classes.row}>
            <Typography component="span" variant="subtitle2" className={classes.label}>
              {t('orders:address')}
            </Typography>
            <Typography component="span" variant="subtitle1" className={classes.value}>
              {order.address || t('common:no-property-address')}
            </Typography>
          </Box>

          {/* Purchase Price and Loan Amount */}
          {moneyItems.length > 0 && (
            <Box className={classes.moneyRow}>
              {moneyItems}
            </Box>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

type OrdersCardsProps = {
  t: TFunction;
  orders: OrderListingType[];
  handleSelectRow: (id: string, code: string) => void;
  agencyId: string;
};

const OrdersCards: FC<OrdersCardsProps> = ({
  t,
  orders,
  handleSelectRow,
  agencyId,
}) => {
  const showAgency = agencyId === '';

  return (
    <Box>
      {orders.map((order) => (
        <OrderCard
          key={order._id}
          t={t}
          order={order}
          handleSelectRow={handleSelectRow}
          showAgency={showAgency}
        />
      ))}
    </Box>
  );
};

export default OrdersCards;
